import {
  AppBar,
  Divider,
  Button,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  Theme,
  Box
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useContext, useState, FC, useEffect, useMemo } from 'react';
import { Page, PageTitleWithButton } from '../../../components';
import { AdminContext } from '../../../context/admin';
import { InternalUsers } from './internal-users';
import { ExternalUsers } from './external-users';
import { UserContext } from '../../../context';
import { FeatureFlag, UserRoles } from '../../../models';
import { EntraInternalUsers } from './entra-internal-users';
import { useFlags, useAllowedNavRoutes } from '../../../hooks';
import { getCurrentNavigationArea } from '../../../helpers/axios';
import { useHistory, useParams } from 'react-router-dom';

type manageTabs = 'internal' | 'external';
enum manageTabsValues {
  internal,
  external
}

interface IManageAdminUsers {
  isVendorAffiliate?: boolean;
}

export const ManageAdminUsers: FC<IManageAdminUsers> = ({ isVendorAffiliate }) => {
  const featureFlags = useFlags();
  if (featureFlags.authorizeUserNavigationAccess) {
    return <ManageAdminUsersDynamic isVendorAffiliate={isVendorAffiliate} />;
  } else {
    return <ManageAdminUsersStatic isVendorAffiliate={isVendorAffiliate} />;
  }
};

export const ManageAdminUsersStatic: FC<IManageAdminUsers> = ({ isVendorAffiliate }) => {
  const classes = useStyles();
  const { selectedUsersTab, setSelectedUsersTab } = useContext(AdminContext);
  const [isAddEditExternalUserModalOpen, setIsAddEditExternalUserModalOpen] = useState(false);
  const [isActive, setActive] = useState<boolean>(true);

  // Check if personType URL query is provided for the table.
  const params = new URLSearchParams(window.location.search);
  const personType = params.get('personType');
  const personId = params.get('personId');
  const displayName = params.get('displayName');
  const tab = params.get('tab');
  const [externalPage, setExternalPage] = useState<number>(0);

  const title = isVendorAffiliate ? 'Vendor Affiliates' : 'Manage Users';
  const featureFlags = useFlags();

  const { user } = useContext(UserContext);
  const isBLAdmin = user?.userRoles?.includes(UserRoles.BLAdmin);

  useEffect(() => {
    if (!isBLAdmin && selectedUsersTab === 0) {
      setSelectedUsersTab(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedUsersTab]);

  useEffect(() => {
    if (tab && selectedUsersTab === 0) {
      setSelectedUsersTab(tab ? parseInt(tab) : 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const displayEntraInternalUsers = () => {
    return featureFlags[FeatureFlag.entraId] ? (
      <EntraInternalUsers displayName={displayName} />
    ) : (
      <InternalUsers displayName={displayName} />
    );
  };

  return (
    <Page title={title}>
      <PageTitleWithButton
        pageTitleBackProps={{
          showDivider: false,
          backText: isVendorAffiliate ? 'Back to Vendors home' : 'Back to Admin home',
          backUrl: isVendorAffiliate ? '/vendors' : '/admin',
          title
        }}
        button={
          <Box flexWrap="wrap" display="flex" alignItems="center">
            {(selectedUsersTab === 1 || isVendorAffiliate) && (
              <Box display="flex" gap={1}>
                <Box mr={-1}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={isActive}
                        id="active-inactive"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setActive(e.target.checked);
                          setExternalPage(0);
                        }}
                      />
                    }
                    label="Active"
                  />
                </Box>
                <Divider orientation="vertical" flexItem />
                <Button
                  id="create-new-external-user"
                  onClick={async () => {
                    setIsAddEditExternalUserModalOpen(true);
                  }}
                  color="primary"
                  variant="contained"
                >
                  {isVendorAffiliate ? 'Create New Vendor Affiliate' : 'Create New External User'}
                </Button>
              </Box>
            )}
          </Box>
        }
      />
      {!isVendorAffiliate && (
        <AppBar position="static" color="default" component="div" className={classes.tabBar}>
          <Tabs
            value={selectedUsersTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={(_, value) => {
              setSelectedUsersTab(value);
            }}
          >
            {isBLAdmin && <Tab id="internal" aria-controls="internal-content" label="Internal" />}
            <Tab id="external" aria-controls="external-content" label="External" />
          </Tabs>
        </AppBar>
      )}
      {selectedUsersTab === 0 && !isVendorAffiliate && displayEntraInternalUsers()}

      {(selectedUsersTab === 1 || isVendorAffiliate) && (
        <ExternalUsers
          selectedUsersTab={selectedUsersTab}
          isActive={isActive}
          isAddEditExternalUserModalOpen={isAddEditExternalUserModalOpen}
          setIsAddEditExternalUserModalOpen={setIsAddEditExternalUserModalOpen}
          personType={personType}
          personId={personId}
          setPage={setExternalPage}
          page={externalPage}
          isVendorAffiliate={isVendorAffiliate}
        />
      )}
    </Page>
  );
};

export const ManageAdminUsersDynamic: FC<IManageAdminUsers> = ({ isVendorAffiliate }) => {
  const classes = useStyles();
  const { selectedUsersTab, setSelectedUsersTab } = useContext(AdminContext);
  const [isAddEditExternalUserModalOpen, setIsAddEditExternalUserModalOpen] = useState(false);
  const [isActive, setActive] = useState<boolean>(true);

  // Check if personType URL query is provided for the table.
  const params = new URLSearchParams(window.location.search);
  const personType = params.get('personType');
  const personId = params.get('personId');
  const displayName = params.get('displayName');
  const { tab } = useParams<{ tab: manageTabs }>();
  const [externalPage, setExternalPage] = useState<number>(0);

  const title = isVendorAffiliate ? 'Vendor Affiliates' : 'Manage Users';
  const featureFlags = useFlags();
  const { user } = useContext(UserContext);
  const { availableLinks } = useAllowedNavRoutes(featureFlags, user);
  const history = useHistory();

  // Permissioning system for tabs
  const rootPath = isVendorAffiliate ? '/vendors/vendor-affiliates' : '/admin/user-tools/users';

  const canViewInternal = isVendorAffiliate
    ? false
    : availableLinks.has(getCurrentNavigationArea(`${rootPath}/internal`));
  const canViewExternal = isVendorAffiliate
    ? availableLinks.has(getCurrentNavigationArea(`${rootPath}`))
    : availableLinks.has(getCurrentNavigationArea(`${rootPath}/external`));

  // If vendor affiliate, only show external tab
  const tabLinks = useMemo(() => {
    if (isVendorAffiliate) {
      return [
        {
          name: 'External',
          value: manageTabsValues.external,
          hidden: false
        }
      ];
    }
    return [
      {
        name: 'Internal',
        value: manageTabsValues.internal,
        hidden: !canViewInternal
      },
      {
        name: 'External',
        value: manageTabsValues.external,
        hidden: !canViewExternal
      }
    ].filter(link => !link.hidden);
  }, [canViewInternal, canViewExternal, isVendorAffiliate]);

  // Ensure selected tab is allowed
  useEffect(() => {
    if (!isVendorAffiliate) {
      if (selectedUsersTab === 0 && !canViewInternal && canViewExternal) {
        setSelectedUsersTab(manageTabsValues.external);
      } else if (selectedUsersTab === 1 && !canViewExternal && canViewInternal) {
        setSelectedUsersTab(manageTabsValues.internal);
      }
    }
    if (isVendorAffiliate) {
      setSelectedUsersTab(manageTabsValues.external);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewInternal, canViewExternal, selectedUsersTab, isVendorAffiliate]);

  useEffect(() => {
    if (tab) {
      const tabValue = tab === 'internal' ? manageTabsValues.internal : manageTabsValues.external;
      if (tabLinks.some(link => link.value === tabValue)) {
        setSelectedUsersTab(tabValue);
      } else if (tabLinks.length > 0) {
        setSelectedUsersTab(tabLinks[0].value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, tabLinks]);

  useEffect(() => {
    let tabParam = tab;
    if (tabParam === undefined) {
      if (canViewInternal) {
        tabParam = 'internal';
      } else if (canViewExternal) {
        tabParam = 'external';
      }
    }

    if (tabParam === 'external' && !canViewExternal) {
      tabParam = 'internal';
    } else if (tabParam === 'internal' && !canViewInternal) {
      tabParam = 'external';
    }

    setSelectedUsersTab(
      tabParam === 'internal' ? manageTabsValues.internal : manageTabsValues.external
    );
    if (isVendorAffiliate) {
      history.replace(rootPath);
      return;
    }
    history.replace(`${rootPath}/${tabParam}`);
  }, [
    canViewExternal,
    canViewInternal,
    history,
    isVendorAffiliate,
    rootPath,
    setSelectedUsersTab,
    tab
  ]);

  const displayEntraInternalUsers = () => {
    return featureFlags[FeatureFlag.entraId] ? (
      <EntraInternalUsers displayName={displayName} />
    ) : (
      <InternalUsers displayName={displayName} />
    );
  };

  return (
    <Page title={title}>
      <PageTitleWithButton
        pageTitleBackProps={{
          showDivider: false,
          backText: isVendorAffiliate ? 'Back to Vendors home' : 'Back to Admin home',
          backUrl: isVendorAffiliate ? '/vendors' : '/admin',
          title
        }}
        button={
          <Box flexWrap="wrap" display="flex" alignItems="center">
            {(selectedUsersTab === 1 || isVendorAffiliate) && (
              <Box display="flex" gap={1}>
                <Box mr={-1}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={isActive}
                        id="active-inactive"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setActive(e.target.checked);
                          setExternalPage(0);
                        }}
                      />
                    }
                    label="Active"
                  />
                </Box>
                <Divider orientation="vertical" flexItem />
                <Button
                  id="create-new-external-user"
                  onClick={async () => {
                    setIsAddEditExternalUserModalOpen(true);
                  }}
                  color="primary"
                  variant="contained"
                >
                  {isVendorAffiliate ? 'Create New Vendor Affiliate' : 'Create New External User'}
                </Button>
              </Box>
            )}
          </Box>
        }
      />
      {!isVendorAffiliate && tabLinks.length > 0 && (
        <AppBar position="static" color="default" component="div" className={classes.tabBar}>
          <Tabs
            value={selectedUsersTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={(_, value) => {
              setSelectedUsersTab(value);
              history.replace(
                `${rootPath}/${value === manageTabsValues.internal ? 'internal' : 'external'}`
              );
            }}
          >
            {tabLinks.map(link => (
              <Tab
                key={link.name}
                id={link.name.toLowerCase()}
                aria-controls={`${link.name.toLowerCase()}-content`}
                label={link.name}
                value={link.value}
              />
            ))}
          </Tabs>
        </AppBar>
      )}
      {selectedUsersTab === 0 &&
        !isVendorAffiliate &&
        canViewInternal &&
        displayEntraInternalUsers()}

      {(selectedUsersTab === 1 || isVendorAffiliate) && canViewExternal && (
        <ExternalUsers
          selectedUsersTab={selectedUsersTab}
          isActive={isActive}
          isAddEditExternalUserModalOpen={isAddEditExternalUserModalOpen}
          setIsAddEditExternalUserModalOpen={setIsAddEditExternalUserModalOpen}
          personType={personType}
          personId={personId}
          setPage={setExternalPage}
          page={externalPage}
          isVendorAffiliate={isVendorAffiliate}
        />
      )}
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  tabBar: {
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    marginTop: '-1rem'
  }
}));
